/**
 * Web SetupWizard Component
 *
 * Setup wizard for beginning a new Bible walkthrough
 */

import React, { useState } from 'react';
import { useTheme } from '../../services/ThemeContext';
import { useSettings } from '../../services/SettingsContext';
import { deleteAutosave } from '../../services/storageService';
import { GameState } from '../../constants/types';
import SettingsIconButton from './Helpers/SettingsIconButton.web';

interface SetupWizardProps {
  onStart: (state: GameState) => void;
  onImport: () => void;
  autosaves: any[];
  onLoadGame: (id: string) => void;
  onClose: () => void;
  onRefreshAutosaves: () => void;
  onOpenSettings: () => void;
}

function SetupWizard({
  onStart,
  onImport,
  autosaves,
  onLoadGame,
  onClose,
  onRefreshAutosaves,
  onOpenSettings,
}: SetupWizardProps) {
  const { currentTheme: colors, fontFamily } = useTheme();
  const { transparencySettings } = useSettings();

  // State matching mobile
  const [mode, setMode] = useState<'immersive' | 'examine'>('immersive');
  const [bibleVerse, setBibleVerse] = useState('');
  const [verseFocus, setVerseFocus] = useState('');
  const [showExistingStories, setShowExistingStories] = useState(false);

  // Tooltip state for floating tooltips on empty inputs
  const [showPassageTooltip, setShowPassageTooltip] = useState(false);
  const [showVerseFocusTooltip, setShowVerseFocusTooltip] = useState(false);
  const [isPassageFocused, setIsPassageFocused] = useState(false);
  const [isVerseFocusFocused, setIsVerseFocusFocused] = useState(false);

  // Check if Bible verse is entered (not empty or just whitespace)
  const hasVerse = bibleVerse.trim().length > 0;

  const handleStart = () => {
    if (!bibleVerse.trim()) {
      alert('Please enter a Bible verse or topic');
      return;
    }
    onStart({
      mode,
      bibleVerse,
      bibleVerseText: '', // Will be populated by AI
      customDirectives: verseFocus,
      status: 'playing',
      history: [],
      turnNumber: 0,
      id: bibleVerse.trim(),
      theme: '',
    } as GameState);
  };

  const handleDeleteAutosave = async (id: string) => {
    await deleteAutosave(id);
    onRefreshAutosaves();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: '100vh' }}>
      <div style={{ flex: 1, maxWidth: 500, width: '100%', margin: '0 auto', position: 'relative', minHeight: '100vh' }}>
        {/* Close button - top left */}
        {onClose && (
          <button
            data-testid="wizard-close-button"
            onClick={onClose}
            style={{
              position: 'absolute',
              top: 20,
              left: 20,
              width: 40,
              height: 40,
              borderRadius: 20,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              border: 'none',
              color: colors.primary,
              fontSize: 24,
              fontWeight: 'bold',
              cursor: 'pointer',
              zIndex: 100,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            ✕
          </button>
        )}

        {/* Settings button - top right, inside container */}
        <SettingsIconButton
          onClick={onOpenSettings}
          data-testid="wizard-settings-button"
          style={{ position: 'absolute', top: 20, right: 20, zIndex: 100 }}
        />

        {/* Scrollable Content */}
        <div
          style={{
            position: 'relative',
            zIndex: 10,
            padding: '40px 20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          {/* Glass Card Container */}
          <div
            style={{
              width: '90%',
              maxWidth: 600,
              padding: 32,
              borderRadius: 16,
              borderWidth: 1,
              borderColor: colors.border,
              backgroundColor: `rgba(30, 41, 59, ${transparencySettings.textAreaOpacity / 100})`,
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: '0 10px 20px rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Existing Stories Panel */}
            {autosaves && autosaves.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <button
                  data-testid="wizard-existing-passages-toggle"
                  onClick={() => setShowExistingStories(!showExistingStories)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: 12,
                    backgroundColor: `${colors.background}40`,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: colors.border,
                    borderStyle: 'solid',
                    color: colors.textMain,
                    fontSize: 14,
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  <span>
                    Continue Existing Passage{autosaves.length === 1 ? '' : 's'} ({autosaves.length})
                  </span>
                  <span>{showExistingStories ? '▼' : '▶'}</span>
                </button>

                {showExistingStories && (
                  <div
                    style={{
                      marginTop: 10,
                      borderWidth: 1,
                      borderColor: colors.border,
                      borderStyle: 'solid',
                      borderRadius: 8,
                      backgroundColor: `${colors.background}20`,
                      maxHeight: 300,
                    }}
                  >
                    <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                      {autosaves.map((save: any) => (
                        <div
                          key={save.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: 12,
                            borderBottomWidth: 1,
                            borderBottomColor: `${colors.border}50`,
                            borderBottomStyle: 'solid',
                          }}
                        >
                          <button
                            onClick={() => onLoadGame && onLoadGame(save.id)}
                            style={{
                              flex: 1,
                              background: 'none',
                              border: 'none',
                              color: colors.textMain,
                              fontSize: 14,
                              fontWeight: '600',
                              textAlign: 'left',
                              cursor: 'pointer',
                              padding: 0,
                            }}
                          >
                            <div>{save.name}</div>
                            <div style={{ fontSize: 12, color: colors.textMuted }}>
                              {save.bibleVerse}
                            </div>
                          </button>
                          <button
                            onClick={() => handleDeleteAutosave(save.id)}
                            style={{
                              marginLeft: 12,
                              padding: 8,
                              background: 'none',
                              border: 'none',
                              color: '#ef4444',
                              fontSize: 18,
                              fontWeight: 'bold',
                              cursor: 'pointer',
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty state when no passages */}
            {(!autosaves || autosaves.length === 0) && (
              <div
                style={{
                  marginBottom: 20,
                  padding: 20,
                  backgroundColor: `${colors.background}20`,
                  borderRadius: 8,
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                  alignItems: 'center',
                }}
              >
                <div
                  style={{
                    color: colors.textMuted,
                    fontSize: 14,
                    fontStyle: 'italic',
                    textAlign: 'center',
                  }}
                >
                  Begin a Bible verse walkthrough experience.
                </div>
              </div>
            )}


            {/* Passage or Topic Reference (Required) */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 10,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  marginBottom: 8,
                  color: colors.textMuted,
                }}
              >
                Passage or Topic *
              </label>
              <input
                data-testid="wizard-passage-input"
                type="text"
                value={bibleVerse}
                onChange={e => {
                  setBibleVerse(e.target.value);
                  setShowPassageTooltip(e.target.value.trim().length === 0);
                }}
                onFocus={() => setIsPassageFocused(true)}
                onBlur={() => {
                  setIsPassageFocused(false);
                  setShowPassageTooltip(false);
                }}
                placeholder="e.g., John 3:16 or Love"
                className="app-input"
                style={{
                  width: '100%',
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                  borderRadius: 8,
                  paddingLeft: 16,
                  paddingRight: 16,
                  paddingTop: 10,
                  paddingBottom: 10,
                  fontSize: 'var(--app-font-size)',
                  fontFamily: fontFamily,
                  backgroundColor: colors.surface,
                  color: colors.textMain,
                }}
              />
              {isPassageFocused && !hasVerse && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    left: 16,
                    backgroundColor: colors.textMain,
                    color: colors.background,
                    padding: '8px 12px',
                    borderRadius: 6,
                    fontSize: 12,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    zIndex: 1000,
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Enter a Bible verse or topic to begin
                </div>
              )}
            </div>

            {/* Mode */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 10,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  marginBottom: 8,
                  color: colors.textMuted,
                }}
              >
                Mode
              </label>
              <div
                style={{
                  display: 'flex',
                  borderRadius: 8,
                  overflow: 'hidden',
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                }}
              >
                <button
                  data-testid="wizard-mode-immersive"
                  onClick={() => setMode('immersive')}
                  title="Story mode where you can take part in the biblical scene"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: mode === 'immersive' ? colors.primary : colors.surface,
                    border: 'none',
                    color: mode === 'immersive' ? '#ffffff' : colors.textMain,
                    fontSize: 14,
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Immersive
                </button>
                <button
                  data-testid="wizard-mode-examine"
                  onClick={() => setMode('examine')}
                  title="Study mode to understand the environment and context"
                  style={{
                    flex: 1,
                    padding: '12px 16px',
                    backgroundColor: mode === 'examine' ? colors.primary : colors.surface,
                    border: 'none',
                    color: mode === 'examine' ? '#ffffff' : colors.textMain,
                    fontSize: 14,
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Examine
                </button>
              </div>
            </div>

            {/* Verse Focus (Optional) */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 10,
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: 1.5,
                  marginBottom: 8,
                  color: colors.textMuted,
                }}
              >
                Verse Focus (Optional)
              </label>
              <textarea
                data-testid="wizard-verse-focus-input"
                value={verseFocus}
                onChange={e => {
                  setVerseFocus(e.target.value);
                  setShowVerseFocusTooltip(e.target.value.trim().length === 0);
                }}
                onFocus={() => setIsVerseFocusFocused(true)}
                onBlur={() => {
                  setIsVerseFocusFocused(false);
                  setShowVerseFocusTooltip(false);
                }}
                rows={2}
                placeholder="What aspects of this verse would you like to explore?"
                className="app-input"
                style={{
                  width: '100%',
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                  borderRadius: 8,
                  padding: '8px 12px',
                  fontSize: 'var(--app-font-size)',
                  fontFamily: fontFamily,
                  backgroundColor: colors.surface,
                  color: colors.textMain,
                  minHeight: 56,
                  resize: 'vertical',
                }}
              />
              {isVerseFocusFocused && verseFocus.trim().length === 0 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 'calc(100% + 8px)',
                    left: 16,
                    backgroundColor: colors.textMain,
                    color: colors.background,
                    padding: '8px 12px',
                    borderRadius: 6,
                    fontSize: 12,
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                    zIndex: 1000,
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Describe your focus area (optional)
                </div>
              )}
            </div>

            {/* Disclaimer */}
            <div style={{ marginTop: 16, marginBottom: 8, paddingLeft: 16,
                  paddingRight: 16 }}>
              <div
                style={{
                  color: colors.textMuted,
                  fontStyle: 'italic',
                  textAlign: 'center',
                  lineHeight: '20px',
                  fontFamily: fontFamily,
                  fontSize: 'var(--app-font-size)',
                }}
              >
                Note: The generated stories draw from historical data and conservative biblical
                commentaries. Interpretations beyond the actual verse text may vary and should
                be studied further for deeper understanding.
              </div>
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 16, marginTop: 24 }}>
              <button
                data-testid="wizard-begin-walkthrough"
                onClick={handleStart}
                disabled={!hasVerse}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  borderRadius: 10,
                  backgroundColor: hasVerse ? colors.primary : colors.textMuted,
                  border: 'none',
                  color: '#ffffff',
                  fontSize: 14,
                  fontWeight: '600',
                  cursor: hasVerse ? 'pointer' : 'not-allowed',
                  whiteSpace: 'nowrap',
                  opacity: hasVerse ? 1 : 0.5,
                }}
              >
                Begin Walkthrough
              </button>
              <button
                data-testid="wizard-load-walkthrough"
                onClick={onImport}
                style={{
                  flex: 1,
                  padding: '12px 14px',
                  borderRadius: 10,
                  backgroundColor: 'transparent',
                  borderWidth: 1,
                  borderColor: colors.border,
                  borderStyle: 'solid',
                  color: colors.textMain,
                  fontSize: 14,
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                Load Walkthrough
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SetupWizard;
